import type {
  ScheduledReport,
  ScheduledReportListResponse,
  ScheduledReportOption,
  ScheduledReportOptions,
} from "api/models";
import { ScheduledReportFormValues } from "./scheduledReportTypes";

export const DAILY_FREQUENCY = "4";
export const BILLING_TYPE = "shipper_billing";
export const SHIPMENT_DETAIL_TYPE = "shipment_detail";
export const CUSTOM_RANGE = "custom_date_range";
export const DEFAULT_EXECUTION_TIME = "08:00:00";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const parseAdditionalEmails = (value?: string) =>
  (value || "")
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);

export const findInvalidAdditionalEmail = (value: string) =>
  parseAdditionalEmails(value).find((email) => !EMAIL_PATTERN.test(email));

export const normalizeExecutionTime = (value?: string | null) => {
  if (!value) return DEFAULT_EXECUTION_TIME;
  if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
  if (/^\d{2}:\d{2}:\d{2}/.test(value)) return value.slice(0, 8);
  return DEFAULT_EXECUTION_TIME;
};

export const getShipmentDetailOptionId = (field: ScheduledReportOption) => String(field.key || field.value);

export const mapShipmentDetailFieldsToForm = (
  selected: Array<string | number> = [],
  fieldOptions: ScheduledReportOption[] = [],
) =>
  selected.map((value) => {
    const field = fieldOptions.find(
      (option) => String(option.value) === String(value) || String(option.key || "") === String(value),
    );
    return field ? getShipmentDetailOptionId(field) : String(value);
  });

export const getScheduledReportList = (data?: ScheduledReportListResponse): ScheduledReport[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.results || [];
};

export const getDefaultFormValues = (
  isProvider: boolean,
  options?: ScheduledReportOptions,
): ScheduledReportFormValues => ({
  report_name: "",
  report_type: String(options?.report_types[0]?.value || (isProvider ? SHIPMENT_DETAIL_TYPE : "freight_mode")),
  data_range: "this_week",
  data_from: null,
  data_to: null,
  analytics_filter_by: "week",
  filter_date_range_by: "book_date",
  report_frequency: String(options?.frequencies[0]?.value || 1),
  execution_time: DEFAULT_EXECUTION_TIME,
  shipment_detail_fields: [],
  included_companies: [],
  additional_emails: "",
});

export const mapReportToFormValues = (
  report: ScheduledReport,
  options: ScheduledReportOptions,
): ScheduledReportFormValues => {
  const detailKeys = mapShipmentDetailFieldsToForm(report.shipment_detail_fields, options.shipment_detail_fields);

  return {
    report_name: report.report_name,
    report_type: report.report_type,
    data_range: report.data_range,
    data_from: report.data_from || null,
    data_to: report.data_to || null,
    analytics_filter_by: report.analytics_filter_by || "week",
    filter_date_range_by: report.filter_date_range_by || "book_date",
    report_frequency: String(report.report_frequency),
    execution_time: normalizeExecutionTime(report.execution_time),
    shipment_detail_fields: detailKeys.map(String),
    included_companies: (report.included_companies || []).map(String),
    additional_emails: (report.additional_emails || []).join(", "),
  };
};

export const buildScheduledReportPayload = (
  formData: ScheduledReportFormValues,
  options: ScheduledReportOptions | undefined,
  isProvider: boolean,
) => {
  const shipmentValues = formData.shipment_detail_fields.map(
    (key) => options?.shipment_detail_fields.find((field) => (field.key || field.value) === key)?.value || key,
  );
  const payload: Record<string, unknown> = {
    report_name: formData.report_name,
    report_type: formData.report_type,
    data_range: formData.data_range,
    report_frequency: Number(formData.report_frequency),
    execution_time: formData.execution_time,
    additional_emails: parseAdditionalEmails(formData.additional_emails),
  };

  if (formData.data_range === CUSTOM_RANGE) {
    payload.data_from = formData.data_from;
    payload.data_to = formData.data_to;
  }

  if (formData.report_type === BILLING_TYPE) {
    payload.filter_date_range_by = "book_date";
  } else {
    payload.analytics_filter_by = formData.analytics_filter_by;
    payload.filter_date_range_by = formData.filter_date_range_by;
  }

  if (!isProvider && formData.report_type === SHIPMENT_DETAIL_TYPE) {
    payload.shipment_detail_fields = shipmentValues;
  }

  if (isProvider && formData.report_type === SHIPMENT_DETAIL_TYPE) {
    payload.included_companies = formData.included_companies.map(Number);
  }

  return payload;
};
